import { Link } from 'react-router-dom';

const features = [
  {
    icon: 'ri-price-tag-3-line',
    title: 'Category Tracking',
    description: 'Track deals across 14 product categories including Shoes, Hoodies, T-Shirts, Jackets, and more — all in one LitBuy Spreadsheet.',
    image: 'https://readdy.ai/api/search-image?query=Colorful%20organized%20closet%20with%20neatly%20arranged%20shoes%20hoodies%20and%20accessories%20on%20open%20shelving%2C%20bright%20natural%20daylight%20streaming%20through%20window%2C%20modern%20wardrobe%20organization%2C%20vibrant%20diverse%20colors%2C%20editorial%20interior%20photography&width=600&height=400&seq=feature-img-track-01&orientation=landscape',
    link: '/blog/litbuy-spreadsheet-complete-guide',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Price Comparison',
    description: 'Compare prices instantly across categories. LitBuy Spreadsheet makes it effortless to spot the best deals before you buy.',
    image: 'https://readdy.ai/api/search-image?query=Modern%20smartphone%20displaying%20price%20comparison%20charts%20with%20shopping%20bags%20in%20background%2C%20clean%20wooden%20desk%20surface%2C%20warm%20morning%20light%2C%20tech%20lifestyle%20photography%2C%20minimal%20composition%2C%20fresh%20contemporary%20aesthetic&width=600&height=400&seq=feature-img-compare-01&orientation=landscape',
    link: '/blog/litbuy-spreadsheet-vs-google-sheets',
  },
  {
    icon: 'ri-refresh-line',
    title: 'Inventory Sync',
    description: 'Keep your shopping list synchronized and up to date. LitBuy Spreadsheet ensures you never miss a restock or price drop.',
    image: 'https://readdy.ai/api/search-image?query=Multiple%20devices%20showing%20synchronized%20shopping%20data%20on%20a%20bright%20workspace%2C%20laptop%20tablet%20and%20phone%20in%20harmony%2C%20clean%20white%20desk%20with%20green%20plant%2C%20modern%20tech%20ecosystem%2C%20soft%20afternoon%20lighting%2C%20productive%20atmosphere&width=600&height=400&seq=feature-img-sync-01&orientation=landscape',
    link: '/blog/litbuy-spreadsheet-setup-guide',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: 'Deal Analytics',
    description: 'Visualize your savings with built-in charts and reports. LitBuy Spreadsheet turns raw data into actionable shopping insights.',
    image: 'https://readdy.ai/api/search-image?query=Futuristic%20data%20dashboard%20with%20colorful%20bar%20charts%20and%20graphs%20displayed%20on%20a%20large%20monitor%2C%20modern%20office%20setup%2C%20business%20analytics%20visualization%2C%20clean%20minimal%20environment%2C%20soft%20blue%20and%20green%20accent%20lighting%2C%20professional%20workspace&width=600&height=400&seq=feature-img-analytics-01&orientation=landscape',
    link: '/blog/top-features-litbuy-spreadsheet',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Ready Templates',
    description: 'Start immediately with pre-built LitBuy Spreadsheet templates designed for every shopping scenario and category.',
    image: 'https://readdy.ai/api/search-image?query=Beautifully%20designed%20document%20templates%20spread%20across%20a%20bright%20white%20desk%2C%20colorful%20sticky%20notes%20and%20pens%2C%20creative%20workspace%20vibe%2C%20organized%20papers%20with%20clean%20typography%2C%20morning%20sunlight%2C%20modern%20stationery%20flat%20lay&width=600&height=400&seq=feature-img-templates-01&orientation=landscape',
    link: '/blog/best-litbuy-spreadsheet-templates',
  },
  {
    icon: 'ri-shield-flash-line',
    title: 'Safe & Reliable',
    description: 'Your data stays private. LitBuy Spreadsheet operates with transparency — no hidden trackers or unauthorized access.',
    image: 'https://readdy.ai/api/search-image?query=Shield%20icon%20floating%20above%20a%20secure%20digital%20lock%20on%20a%20glowing%20circuit%20board%20background%2C%20cybersecurity%20theme%2C%20trust%20and%20protection%20symbolism%2C%20warm%20amber%20and%20green%20glow%2C%20abstract%20tech%20security%20concept%2C%20modern%203D%20render&width=600&height=400&seq=feature-img-safe-01&orientation=landscape',
    link: '/blog/common-mistakes-litbuy-spreadsheet',
  },
];

export default function FeatureGrid() {
  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-primary-500 mb-4">Why LitBuy Spreadsheet</span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
            Smart Tools for<br />Modern Shopping
          </h2>
          <p className="mt-4 text-foreground-600 max-w-[560px] mx-auto text-sm md:text-base leading-relaxed">
            LitBuy Spreadsheet combines powerful tracking features with an intuitive design, helping thousands of shoppers make smarter purchasing decisions every day.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {features.map((feature) => (
            <Link
              to={feature.link}
              key={feature.title}
              className="group rounded-2xl bg-background-50 border border-background-200/60 hover:border-primary-200 hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer block"
            >
              {/* Feature Image */}
              <div className="w-full aspect-[3/2] overflow-hidden">
                <img
                  src={feature.image}
                  alt={feature.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 md:p-7">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4 group-hover:bg-accent-200 transition-colors">
                  <i className={`${feature.icon} text-lg`}></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {feature.description}
                </p>
                {/* 阅读更多指示器 */}
                <div className="mt-4 flex items-center gap-1 text-xs font-medium text-primary-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span>Read More</span>
                  <i className="ri-arrow-right-line text-xs"></i>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}